import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw, ShieldCheck, Undo2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { Skeleton } from "./ui/skeleton";
import { api, errMsg, fmtINR } from "../lib/api";
import { pokeLiveCollections } from "../lib/liveCollections";

/**
 * Phase 107 — roll back a whole legacy payment batch.
 * Every payment written by the batch moves to the recycle bin (payments_reversed), its money goes back
 * on the demands it had settled, advance credit it created is taken back and collector assignments
 * follow the ledger again. Preview → optional reason → confirm → live progress (polled) → done.
 */
export function UndoPaymentBatchDialog({ code, batch, open, onOpenChange, onDone, testId = "undo-payment-batch" }) {
  const batchId = batch?.id;
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [undo, setUndo] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!open || !batchId) return;
    let cancelled = false;
    setPreview(null); setPreviewError(null); setUndo(null); setBusy(false); setReason("");
    api.get(`/t/${code}/payment-import/${batchId}/undo-preview`)
      .then((r) => {
        if (cancelled) return;
        setPreview(r.data);
        if (r.data.undo_status === "processing") setUndo(r.data.undo || { status: "processing" });
        if (r.data.undo_status === "completed") setUndo(r.data.undo);
      })
      .catch((e) => { if (!cancelled) setPreviewError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [open, code, batchId]);

  useEffect(() => {
    if (!open || !batchId || undo?.status !== "processing") return;
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        const r = await api.get(`/t/${code}/payment-import/${batchId}`, { timeout: 15000 });
        if (stopped) return;
        const u = r.data.undo || {};
        setUndo(u);
        if (u.status === "completed") {
          toast.success(`${u.processed} payment${u.processed === 1 ? "" : "s"} rolled back — ${fmtINR(u.amount || 0)} put back on the demands`);
          pokeLiveCollections();   // Phase 149
          doneRef.current?.(u);
          return;
        }
        if (u.status === "failed") return;
      } catch { /* transient — keep polling */ }
      if (!stopped) timer = setTimeout(poll, 800);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [open, code, batchId, undo?.status]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/payment-import/${batchId}/undo`, { reason });
      setUndo(r.data.undo || { status: "processing" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const running = undo?.status === "processing";
  const finished = undo?.status === "completed" || undo?.status === "failed";
  const pct = undo?.total ? Math.min(100, Math.round(((undo.processed || 0) / undo.total) * 100)) : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!running) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Undo2 className="h-4 w-4" /> Undo this payment batch</DialogTitle>
          <DialogDescription className="break-all" data-testid={`${testId}-file`}>{batch?.filename || "Payment import"} · Batch ID: {batchId}</DialogDescription>
        </DialogHeader>

        {previewError && (
          <Alert variant="destructive" data-testid={`${testId}-preview-error`}>
            <AlertTitle>Could not load the preview</AlertTitle>
            <AlertDescription>{previewError}</AlertDescription>
          </Alert>
        )}
        {!preview && !previewError && (
          <div className="space-y-2" data-testid={`${testId}-loading`}><Skeleton className="h-5 w-3/4" /><Skeleton className="h-16 w-full" /></div>
        )}

        {preview && !running && !finished && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg border p-2">
                <p className="text-lg font-semibold tabular-nums" data-testid={`${testId}-count`}>{preview.payments}</p>
                <p className="text-[11px] text-muted-foreground">Payments reversed</p>
              </div>
              <div className="rounded-lg border p-2">
                <p className="text-lg font-semibold tabular-nums" data-testid={`${testId}-amount`}>{fmtINR(preview.amount)}</p>
                <p className="text-[11px] text-muted-foreground">Back on demands</p>
              </div>
              <div className="rounded-lg border p-2">
                <p className={`text-lg font-semibold tabular-nums ${preview.advance > 0 ? "text-amber-700" : ""}`} data-testid={`${testId}-advance`}>{fmtINR(preview.advance)}</p>
                <p className="text-[11px] text-muted-foreground">Advance taken back</p>
              </div>
            </div>
            {preview.sample_receipts?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testId}-sample`}>
                <span className="text-[11px] text-muted-foreground">e.g.</span>
                {preview.sample_receipts.map((r) => <Badge key={r} variant="outline" className="font-mono text-[10px]">{r}</Badge>)}
              </div>
            )}
            {preview.missing > 0 && (
              <p className="text-xs text-muted-foreground" data-testid={`${testId}-missing`}>{preview.missing} payment{preview.missing === 1 ? " was" : "s were"} already removed earlier and will be skipped.</p>
            )}
            {preview.replaced_records > 0 && (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid={`${testId}-replaced`}>
                This batch replaced <b className="tabular-nums">{preview.replaced_records}</b> existing entr{preview.replaced_records === 1 ? "y" : "ies"} — rolling it back <b>restores</b> {preview.replaced_records === 1 ? "that entry" : "those entries"} (original receipt numbers and amounts).
              </p>
            )}
            {preview.advance_shortfall > 0 && (
              <Alert data-testid={`${testId}-shortfall`}>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Advance credit partly used</AlertTitle>
                <AlertDescription>{fmtINR(preview.advance_shortfall)} of the advance this batch created has since been used and cannot be taken back automatically.</AlertDescription>
              </Alert>
            )}
            {preview.payments === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid={`${testId}-nothing`}>Nothing left to undo — no payments from this batch remain.</p>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor={`${testId}-reason`} className="text-xs">Reason (kept in the audit trail)</Label>
                  <Input id={`${testId}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
                    placeholder="e.g. wrong file — March receipts uploaded twice" data-testid={`${testId}-reason`} />
                </div>
                <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Payments and their receipts move to the recycle bin, the demand balances are restored and collector assignments follow the ledger again. Payments recorded any other way are not touched.
                </p>
              </>
            )}
          </div>
        )}

        {running && (
          <div className="space-y-2" role="status" aria-live="polite" data-testid={`${testId}-progress`}>
            <p className="text-sm font-medium">Rolling back… {undo.total ? `${undo.processed || 0} / ${undo.total}` : ""}</p>
            <Progress value={pct} className="[&>div]:transition-transform [&>div]:duration-200" />
            <p className="text-xs text-muted-foreground">Keep this window open — large batches take a few moments.</p>
          </div>
        )}

        {undo?.status === "completed" && (
          <Alert data-testid={`${testId}-done`}>
            <RotateCcw className="h-4 w-4" />
            <AlertTitle>Batch rolled back</AlertTitle>
            <AlertDescription>
              {undo.processed} payment{undo.processed === 1 ? "" : "s"} ({fmtINR(undo.amount || 0)}) reversed across {undo.properties ?? "—"} propert{undo.properties === 1 ? "y" : "ies"}
              {undo.advance_short > 0 ? `; ${fmtINR(undo.advance_short)} advance could not be taken back (already used)` : ""}.
              {undo.reason ? ` Reason: ${undo.reason}` : ""}
            </AlertDescription>
          </Alert>
        )}
        {undo?.status === "failed" && (
          <Alert variant="destructive" data-testid={`${testId}-failed`}>
            <AlertTitle>Undo stopped with an error</AlertTitle>
            <AlertDescription>{undo.error || "Please try again."} Progress so far is kept; running Undo again continues safely.</AlertDescription>
          </Alert>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running} data-testid={`${testId}-close`}>{finished ? "Close" : "Cancel"}</Button>
          {!finished && (
            <Button variant="destructive" onClick={start} disabled={busy || running || !preview || !preview.can_undo || preview.payments === 0} data-testid={`${testId}-confirm`}>
              <Undo2 className="h-4 w-4 mr-2" /> {running ? "Rolling back…" : preview ? `Undo ${preview.payments} payment${preview.payments === 1 ? "" : "s"}` : "Undo batch"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UndoPaymentBatchDialog;
